import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import {
  CaretDown,
  Chats,
  CloudArrowUp,
  CloudCheck,
  CloudSlash,
  Copy,
  Link as LinkIcon,
  PencilSimpleLine,
  Trash,
} from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useDarkMode } from "../../../hooks/useDarkMode";
import { CustomColorPalette } from "../../../theme";
import { IconButton as CustomIconButton } from "./IconButton";
import useFlowsStore from "../../../store/storeFlows";
import { FlowService } from "../../../service/FlowService";
import { useNotificationStore } from "../../../store/storeNotifications";
import { useNavigate } from "react-router-dom";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";

const AgentNameTypography = styled(Typography)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    fontWeight: 500,
    fontSize: "14px",
    color: isDarkMode ? palette.gray[200] : palette.blue[500],
    textTransform: "capitalize",
    maxWidth: "25rem",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  };
});

const TopBarIconButton = styled(IconButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    width: "24px",
    height: "24px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: isDarkMode ? palette.gray[200] : palette.blue[500],
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
      color: isDarkMode ? palette.gray[200] : palette.blue[500],
    },
  };
});

const SaveStatusIconButton = styled(IconButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    width: "24px",
    height: "24px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: isDarkMode ? palette.gray.A100 : palette.neutral[700],
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.neutral[100],
      color: isDarkMode ? palette.gray.A100 : palette.neutral[900],
    },
  };
});

const MenuItemButton = styled(ListItemButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    display: "flex",
    padding: "4px 8px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
    },
  };
});

const DeleteMenuItemButton = styled(ListItemButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    display: "flex",
    padding: "4px 8px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : "#fecdd3",
    },
  };
});

const MenuItemIconStyled = styled(ListItemIcon)({
  minWidth: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const menuItemTextSx = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 1,
  flex: "1 0 0",
  overflow: "hidden",
  color: isDarkMode ? palette.gray[300] : palette.gray[600],
  fontFeatureSettings: "'liga' off, 'clig' off",
  textOverflow: "ellipsis",
  fontFamily: "Roboto",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  margin: 0,
});

const deleteMenuItemTextSx = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 1,
  flex: "1 0 0",
  overflow: "hidden",
  color: "#b91c1c",
  fontFeatureSettings: "'liga' off, 'clig' off",
  textOverflow: "ellipsis",
  fontFamily: "Roboto",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  margin: 0,
};

interface MenuItemConfig {
  action: () => void;
  icon: PhosphorIcon;
  label: string;
  isDelete?: boolean;
}

interface AgentTopBarProps {
  onChatClick?: () => void;
}

export const AgentTopBar: React.FC<AgentTopBarProps> = ({ onChatClick }) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const saving = useFlowsStore((state) => state.saving);
  const { flows } = useFlowsStore((state) => state);
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  const navigate = useNavigate();
  const params = useParams<{ flowId: string }>();

  const [status, setStatus] = useState({
    isOnline: true,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const activeFlow = flows.find((flow) => flow.id === params.flowId);
  const { addFlow, setFlows } = useFlowsStore((state) => state);

  useEffect(() => {
    const fetchFlowIfNeeded = async () => {
      if (params.flowId && !activeFlow) {
        try {
          const response = await FlowService.getFlow(params.flowId);
          addFlow(response.data);
        } catch (error) {
          console.error("Failed to load flow:", error);
        }
      }
    };

    fetchFlowIfNeeded();
  }, [params.flowId, activeFlow, addFlow]);

  useEffect(() => {
    const handleOnline = () =>
      setStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setStatus((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    setStatus((prev) => ({ ...prev, ...saving }));

    if (!saving.finished && saving.status === "error") {
      setNotification({
        type: "error",
        message: "Error saving agent. Please refresh the page.",
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [saving, setNotification]);

  const getTooltipText = () => {
    if (saving.lastSaved && saving.status === "saved") {
      return `Last saved on: ${moment(saving.lastSaved).format("LT")} ${moment(
        saving.lastSaved,
      ).format("L")}`;
    }
    return "";
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = async () => {
    handleClose();
    if (!activeFlow) return;

    const newName = prompt("Enter new name:", activeFlow.name);
    if (!newName || newName.trim() === activeFlow.name) return;

    try {
      await FlowService.updateFlow(activeFlow.id, { name: newName.trim() });
      window.dispatchEvent(new CustomEvent("sidebar-refresh"));
      setNotification({
        type: "success",
        message: "Agent renamed successfully",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to rename agent",
      });
    }
  };

  const handleShare = () => {
    handleClose();
    if (!activeFlow) return;
    setNotification({
      type: "info",
      message: "Share functionality coming soon",
    });
  };

  const handleDuplicate = async () => {
    handleClose();
    if (!activeFlow) return;

    try {
      const createResponse = await FlowService.duplicateFlow({
        flowId: activeFlow.id,
        name: `${activeFlow.name} (Copy)`,
      });
      setNotification({
        type: "success",
        message: "Agent duplicated successfully",
      });
      navigate(`/dashboard-beta/agent/${createResponse.data.id}`);
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to duplicate agent",
      });
    }
  };

  const handleDelete = async () => {
    handleClose();
    if (!activeFlow) return;

    if (
      !window.confirm(
        "Are you sure you want to delete the agent? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await FlowService.deleteFlow(activeFlow.id);
      window.dispatchEvent(new CustomEvent("sidebar-refresh"));
      setNotification({
        type: "success",
        message: "Agent deleted successfully",
      });
      navigate("/dashboard-beta");
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to delete agent",
      });
    }
  };

  const handleChatClick = () => {
    if (onChatClick) {
      onChatClick();
    } else {
      console.warn("onChatClick not provided to AgentTopBar");
    }
  };

  const textSx = menuItemTextSx(isDarkMode, palette);

  const popoverMenuItems: MenuItemConfig[] = [
    { action: handleRename, icon: PencilSimpleLine, label: "Rename" },
    { action: handleShare, icon: LinkIcon, label: "Share Agent" },
    { action: handleDuplicate, icon: Copy, label: "Duplicate" },
    { action: handleDelete, icon: Trash, label: "Delete Agent", isDelete: true },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
          borderBottom: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[200]
          }`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "48px",
            paddingX: "16px",
            gap: "16px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AgentNameTypography title={activeFlow?.name}>
              {activeFlow?.name || "Loading..."}
            </AgentNameTypography>
            <TopBarIconButton onClick={handleClick}>
              <CaretDown size={16} />
            </TopBarIconButton>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
            }}
          >
            {!saving.finished && saving.status === "loading" && (
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? palette.gray[400] : palette.gray[600],
                  fontSize: "12px",
                  paddingRight: "8px",
                }}
              >
                Auto saving agent
              </Typography>
            )}

            <Tooltip title={getTooltipText()} arrow placement="bottom">
              <SaveStatusIconButton>
                {!saving.finished && saving.status === "loading" ? (
                  <CloudArrowUp
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                ) : status.isOnline && saving.status === "saved" ? (
                  <CloudCheck
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                ) : (
                  <CloudSlash
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                )}
              </SaveStatusIconButton>
            </Tooltip>

            <CustomIconButton icon={Chats} onClick={handleChatClick} />
          </Box>
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              width: "229px",
              padding: "8px",
              flexDirection: "column",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "8px",
              border: `1px solid ${
                isDarkMode ? palette.gray[700] : palette.gray[300]
              }`,
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              marginTop: "8px",
              boxShadow: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
          }}
        >
          {popoverMenuItems.map((item) => {
            const ItemIcon = item.icon;
            if (item.isDelete) {
              return (
                <DeleteMenuItemButton key={item.label} onClick={item.action}>
                  <MenuItemIconStyled>
                    <ItemIcon size={20} color="#b91c1c" />
                  </MenuItemIconStyled>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: deleteMenuItemTextSx }}
                  />
                </DeleteMenuItemButton>
              );
            }
            return (
              <MenuItemButton key={item.label} onClick={item.action}>
                <MenuItemIconStyled>
                  <ItemIcon
                    size={20}
                    color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                  />
                </MenuItemIconStyled>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ sx: textSx }}
                />
              </MenuItemButton>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};
